import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { payoutService, PayoutBatch, PayoutItem } from '../../services/payout.service'
import toast from 'react-hot-toast'

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-blue-100 text-blue-800',
  SCHEDULED: 'bg-indigo-100 text-indigo-800',
  PROCESSING: 'bg-sky-100 text-sky-800',
  COMPLETED: 'bg-emerald-100 text-emerald-800',
  PARTIALLY_COMPLETED: 'bg-teal-100 text-teal-800',
  FAILED: 'bg-red-100 text-red-800',
  REJECTED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-neutral-100 text-neutral-700',
}

const ITEM_COLORS: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  PROCESSING: 'bg-sky-100 text-sky-800',
  COMPLETED: 'bg-emerald-100 text-emerald-800',
  FAILED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-neutral-100 text-neutral-700',
}

const fmt = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const rm = (amount: string | number | null | undefined) =>
  `RM ${Number(amount ?? 0).toFixed(2)}`

export default function PayoutBatchDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [batch, setBatch] = useState<PayoutBatch | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState<PayoutItem | null>(null)
  const [editForm, setEditForm] = useState({
    bankName: '',
    accountNumber: '',
    accountHolderName: '',
    bankCode: '',
  })

  const load = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      setError(null)
      setBatch(await payoutService.getDetail(id))
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load payout batch')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  const handleMarkTransferred = async (item: PayoutItem) => {
    const reference = window.prompt(
      `Confirm the offline bank transfer to ${item.recipientName} (${rm(item.amount)}).` +
        '\nOptionally enter the bank transfer reference:',
    )
    if (reference === null) return
    setSaving(true)
    try {
      await payoutService.markTransferred(item.id, reference.trim() || undefined)
      toast.success(`Payout to ${item.recipientName} marked as transferred`)
      await load()
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to mark item as transferred')
    } finally {
      setSaving(false)
    }
  }

  const openEdit = (item: PayoutItem) => {
    setEditing(item)
    setEditForm({
      bankName: item.bankName === 'TO BE COLLECTED' ? '' : item.bankName,
      accountNumber: item.accountNumber === 'PENDING' ? '' : item.accountNumber,
      accountHolderName: item.accountHolderName,
      bankCode: item.bankCode ?? '',
    })
  }

  const handleSaveBank = async () => {
    if (!editing) return
    setSaving(true)
    try {
      await payoutService.updateItemBank(editing.id, {
        bankName: editForm.bankName.trim(),
        accountNumber: editForm.accountNumber.trim(),
        accountHolderName: editForm.accountHolderName.trim(),
        bankCode: editForm.bankCode.trim() || undefined,
      })
      toast.success(`Bank details updated for ${editing.recipientName}`)
      setEditing(null)
      await load()
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to update bank details')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = async () => {
    if (!batch) return
    if (!window.confirm(`Cancel batch ${batch.batchNumber}? This cannot be undone.`)) return
    setSaving(true)
    try {
      await payoutService.cancel(batch.id)
      toast.success(`Batch ${batch.batchNumber} cancelled`)
      await load()
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to cancel batch')
    } finally {
      setSaving(false)
    }
  }

  const handleRetryItem = async (item: PayoutItem) => {
    try {
      await payoutService.retryItem(item.id)
      toast.success(`Retrying payout to ${item.recipientName}`)
      await load()
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to retry item')
    }
  }

  const statusPill = (status: string, colors: Record<string, string>) => (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
        colors[status] ?? 'bg-neutral-100 text-neutral-700'
      }`}
    >
      {status.replace(/_/g, ' ')}
    </span>
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 py-8">
        <div className="mx-auto max-w-4xl px-6">
          <p className="text-sm text-neutral-500">Loading…</p>
        </div>
      </div>
    )
  }

  if (error || !batch) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Payout batch not found'}</p>
          <button
            onClick={() => navigate('/admin/claims/payouts')}
            className="px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50"
          >
            Back to Payouts
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="mx-auto max-w-4xl px-6">
        <button
          onClick={() => navigate('/admin/claims/payouts')}
          className="text-sm text-neutral-500 hover:text-neutral-800"
        >
          ← Back to Payouts
        </button>

        <div className="mt-3 flex items-center gap-3">
          <h1 className="font-mono text-2xl font-bold text-neutral-900">{batch.batchNumber}</h1>
          {statusPill(batch.status, STATUS_COLORS)}
        </div>

        <div className="mt-6 space-y-5 rounded-xl border border-neutral-200 bg-white p-6 text-sm">
          <div>
            <h3 className="mb-2 font-semibold text-neutral-900">Batch</h3>
            <p className="text-neutral-700">
              {batch.name} · {batch.type.replace(/_/g, ' ')} · {batch.itemCount} item(s) ·{' '}
              {rm(batch.totalAmount)}
            </p>
            <p className="text-xs text-neutral-500">
              Created {fmt.format(new Date(batch.createdAt))}
              {batch.approvedAt ? ` · Approved ${fmt.format(new Date(batch.approvedAt))}` : ''}
              {batch.processedAt ? ` · Processed ${fmt.format(new Date(batch.processedAt))}` : ''}
            </p>
            {batch.rejectionReason && (
              <p className="mt-2 rounded-lg bg-red-50 p-3 text-red-700">
                Rejected: {batch.rejectionReason}
              </p>
            )}
          </div>

          {batch.statistics && (
            <div>
              <h3 className="mb-2 font-semibold text-neutral-900">Progress</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-lg bg-neutral-50 p-3">
                  <p className="text-xs text-neutral-500">Total</p>
                  <p className="font-semibold">{rm(batch.statistics.totalAmount)}</p>
                </div>
                <div className="rounded-lg bg-neutral-50 p-3">
                  <p className="text-xs text-neutral-500">Completed</p>
                  <p className="font-semibold text-emerald-700">{rm(batch.statistics.completedAmount)}</p>
                </div>
                <div className="rounded-lg bg-neutral-50 p-3">
                  <p className="text-xs text-neutral-500">Pending</p>
                  <p className="font-semibold text-amber-700">{rm(batch.statistics.pendingAmount)}</p>
                </div>
                <div className="rounded-lg bg-neutral-50 p-3">
                  <p className="text-xs text-neutral-500">Failed</p>
                  <p className="font-semibold text-red-700">{rm(batch.statistics.failedAmount)}</p>
                </div>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-neutral-100">
                <div
                  className="h-full rounded-full bg-emerald-500"
                  style={{ width: `${Math.min(100, batch.statistics.completionRate)}%` }}
                />
              </div>
              <p className="mt-1 text-xs text-neutral-500">
                {batch.statistics.completionRate.toFixed(0)}% complete
              </p>
            </div>
          )}

          <div>
            <h3 className="mb-2 font-semibold text-neutral-900">Items</h3>
            <div className="overflow-x-auto rounded-lg border border-neutral-200">
              <table className="w-full text-left text-xs">
                <thead className="bg-neutral-50 text-neutral-500">
                  <tr>
                    <th className="px-3 py-2">Recipient</th>
                    <th className="px-3 py-2">Bank</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Transfer</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {(batch.items ?? []).map((item) => {
                    const bankPending =
                      item.accountNumber === 'PENDING' || item.bankName === 'TO BE COLLECTED'
                    return (
                      <tr key={item.id} className="border-t border-neutral-100">
                        <td className="px-3 py-2">
                          <p className="font-medium text-neutral-900">{item.recipientName}</p>
                          <p className="text-neutral-500">
                            {item.recipientId}
                            {item.recipientEmail ? ` · ${item.recipientEmail}` : ''}
                          </p>
                        </td>
                        <td className="px-3 py-2">
                          <p>{item.bankName}</p>
                          <p className="text-neutral-500">
                            {item.accountHolderName} · {item.accountNumber}
                          </p>
                        </td>
                        <td className="px-3 py-2 text-right font-medium">{rm(item.amount)}</td>
                        <td className="px-3 py-2">
                          {statusPill(item.status, ITEM_COLORS)}
                          {item.statusReason ? (
                            <p className="text-neutral-500">{item.statusReason}</p>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 font-mono text-neutral-500">
                          {item.transferId ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {item.status === 'FAILED' ? (
                            <button
                              onClick={() => handleRetryItem(item)}
                              className="rounded border border-amber-300 px-2 py-1 text-amber-700 hover:bg-amber-50"
                            >
                              Retry
                            </button>
                          ) : item.status === 'PENDING' &&
                            !['CANCELLED', 'REJECTED'].includes(batch.status) ? (
                            <div className="flex flex-col items-end gap-1">
                              <button
                                onClick={() => handleMarkTransferred(item)}
                                disabled={saving || bankPending}
                                title={
                                  bankPending
                                    ? 'Bank details have not been collected for this recipient yet'
                                    : undefined
                                }
                                className="rounded-md bg-[#0D7330] px-3 py-1 text-xs font-medium text-white hover:bg-[#0a5c27] disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                Mark Transferred
                              </button>
                              {bankPending && (
                                <p className="text-[10px] text-amber-600">Bank details pending</p>
                              )}
                              <button
                                onClick={() => openEdit(item)}
                                className="text-[10px] text-neutral-500 underline hover:text-neutral-800"
                              >
                                Edit bank details
                              </button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {batch.status === 'PENDING' && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleCancel}
                disabled={saving}
                className="rounded-md border border-neutral-300 px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50 disabled:opacity-40"
              >
                Cancel Batch
              </button>
            </div>
          )}
          <p className="text-xs text-neutral-400">
            Transfer each amount offline from the company bank account, then mark that recipient as
            transferred. When every item is marked, the batch completes and the source inheritance
            claim becomes PAID.
          </p>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-neutral-200 p-5">
              <div>
                <h2 className="font-semibold text-neutral-900">Bank details</h2>
                <p className="text-xs text-neutral-500">
                  {editing.recipientName} · {rm(editing.amount)}
                </p>
              </div>
              <button
                onClick={() => setEditing(null)}
                className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
              >
                ✕
              </button>
            </div>
            <div className="space-y-3 p-5 text-sm">
              {(
                [
                  ['bankName', 'Bank name'],
                  ['accountNumber', 'Account number'],
                  ['accountHolderName', 'Account holder name'],
                  ['bankCode', 'Bank code (optional)'],
                ] as const
              ).map(([field, label]) => (
                <label key={field} className="block">
                  <span className="mb-1 block text-xs font-medium text-neutral-700">{label}</span>
                  <input
                    value={editForm[field]}
                    onChange={(e) => setEditForm({ ...editForm, [field]: e.target.value })}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-neutral-500 focus:outline-none"
                  />
                </label>
              ))}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setEditing(null)}
                  className="rounded-md border border-neutral-300 px-4 py-2 text-sm text-neutral-600 hover:bg-neutral-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveBank}
                  disabled={
                    saving ||
                    !editForm.bankName.trim() ||
                    !editForm.accountNumber.trim() ||
                    !editForm.accountHolderName.trim()
                  }
                  className="rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-40"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
